import { FALLBACK_COUNTRIES, countryPathForCode, findCountryByUrlPath, getDefaultCountry, getFirstPathSegment, resolveCountryFromPath } from '../../../utils/country';

/**
 * Country code for the market in the URL. The first segment is a url path such as "Sri-Lanka",
 * not a two-letter code, so it is matched against the registry.
 */
export const getCountryFromUrl = (countries = FALLBACK_COUNTRIES) => {
    const segment = getFirstPathSegment(window.location.pathname);
    const match = findCountryByUrlPath(countries, segment);
    return match?.code || getDefaultCountry(countries)?.code;
};

// Gets url path from country code
export const getCountryNameFromCode = (countryCode, countries = FALLBACK_COUNTRIES) =>
    countryPathForCode(countries, countryCode) || countryCode;

// Checks if current page should hide the chatbot
export const isCountryAgnosticRoute = (pathname = window.location.pathname) => {
    const { isCountryAgnostic, country } = resolveCountryFromPath(pathname, FALLBACK_COUNTRIES);
    return isCountryAgnostic || !country;
};

export const shouldHideChatbot = () => {
    if (isCountryAgnosticRoute()) {
        return true;
    }

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;

    // Check for space/bookings pages with eventTypeId parameter
    // This covers both /space/bookings and /Sri-Lanka/space/bookings patterns
    const isBookingPage = currentPath.includes('/space/bookings');
    const hasEventTypeId = currentSearch.includes('eventTypeId');

    // Debug logging (remove in production)
    // if (process.env.NODE_ENV === 'development') {
    //     console.log('Chatbot popup visibility check:', {
    //         currentPath,
    //         currentSearch,
    //         isBookingPage,
    //         hasEventTypeId,
    //         shouldHide: isBookingPage && hasEventTypeId
    //     });
    // }

    return isBookingPage && hasEventTypeId;
};

// Checks if workspace types are loaded from Redux state
export const areWorkspaceTypesLoaded = (spaceListingTypes, spaceActivities, searchResultTypes, homePageTypes) => {
    return spaceListingTypes?.length > 0 ||
        spaceActivities?.length > 0 ||
        searchResultTypes?.length > 0 ||
        homePageTypes?.length > 0;
};

// Constructs booking URL with parameters
export const constructBookingUrl = (params) => {
    const { country, eventTypeId, spaceId, unit = 'hour' } = params;

    // Convert country code to full name if needed
    const countryName = getCountryNameFromCode(country);

    return `/${countryName}/space/bookings?eventTypeId=${eventTypeId}&spaceId=${spaceId}&unit=${unit}`;
};

// Gets current page path and search parameters
export const getCurrentPageInfo = () => {
    return {
        pathname: window.location.pathname,
        search: window.location.search,
        href: window.location.href
    };
};

// Checks if current page matches a specific pattern
export const isCurrentPage = (pattern) => {
    const currentPath = window.location.pathname;

    if (typeof pattern === 'string') {
        return currentPath.includes(pattern);
    } else if (pattern instanceof RegExp) {
        return pattern.test(currentPath);
    }

    return false;
};

// Extracts query parameters from current URL
export const getQueryParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};

    for (const [key, value] of searchParams.entries()) {
        params[key] = value;
    }

    return params;
};

// Checks if a specific query parameter exists and has a value
export const hasQueryParam = (paramName) => {
    const params = getQueryParams();
    return params.hasOwnProperty(paramName) && params[paramName] !== '';
};

// Gets the value of a specific query parameter
export const getQueryParam = (paramName, defaultValue = '') => {
    const params = getQueryParams();
    return params[paramName] || defaultValue;
};
